import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Anchor,
  Box,
  Button,
  Card,
  Checkbox,
  Fieldset,
  Flex,
  Grid,
  Group,
  NumberInput,
  Select,
  TextInput,
  Title,
  Tooltip
} from '@mantine/core';
import { IconCurrencyDollar, IconHelpCircle, IconMapPin, IconPercentage } from '@tabler/icons-react';
import RegimenFiscalEnum from '../../enum/RegimenFiscalEnum';
import PhoneInput from 'react-phone-number-input';
import { forwardRef, useMemo, useState, type ComponentPropsWithoutRef } from 'react';
import { useSelector } from 'react-redux';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import CRUDCardSkeleton from '../../components/Skeletons/CRUDCardSkeleton';
import { useGetStores } from '../../hooks/stores/queries';
import { useGoogleMapsApi } from '../../hooks/useGoogleMapsApi';
import useCanShowFiscalFeature, {
  FiscalFeature
} from '../../hooks/fiscal/useCanShowFiscalFeature';
import {
  buildMapLocationFromLatLng,
  getGoogleMapsUrlFromMapLocation,
  getLatFromMapLocation,
  getLngFromMapLocation
} from '../../utils/storeMapLocation';
import StoreMapLocationPickerModal from '../Stores/components/StoreMapLocationPickerModal';
import { generateFrequentCustomerCardNumber } from '@washday/utilities';

// Define the custom input component with forwardRef
const CustomInput = forwardRef<HTMLInputElement, ComponentPropsWithoutRef<typeof TextInput>>((props, ref) => {
  const { value, onChange, ...rest } = props;
  const { t } = useTranslation(['CustomersPage', 'FieldName']);

  return (
    <TextInput
      value={value}
      onChange={onChange}
      ref={ref}
      label={t('FieldName:phone')}
      placeholder={t('FieldName:phone')}
      {...rest}
    />
  );
});

const CreateEditForm = ({
  form,
  loading,
  isEditing
}: {
  form: any;
  storeId: string;
  loading?: boolean;
  isEditing?: boolean;
}) => {
  const { t } = useTranslation(['CustomersPage', 'FieldName']);
  const {
    company,
    user: { isCompanyAdmin }
  } = useSelector((state: any) => state.user);
  const { data: stores } = useGetStores(true);
  const canShowMexicanCFDIFields = useCanShowFiscalFeature(FiscalFeature.MEXICO_CFDI_FIELDS);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isCardHelpOpen, setIsCardHelpOpen] = useState(false);
  const googleMapsApi = useGoogleMapsApi(true);

  const addressMapLocation = form.getValues().address?.mapLocation || null;
  const latitude = getLatFromMapLocation(addressMapLocation);
  const longitude = getLngFromMapLocation(addressMapLocation);
  const openInGoogleMapsUrl = getGoogleMapsUrlFromMapLocation(addressMapLocation);
  const isGoogleUnavailable = !googleMapsApi.isConfigured || googleMapsApi.isError;
  const googleUnavailableMessage = useMemo(() => {
    if (!googleMapsApi.isConfigured) {
      return t('CustomersPage:googleMapsUnavailable');
    }

    return googleMapsApi.errorMessage || t('CustomersPage:googleMapsUnavailable');
  }, [googleMapsApi.errorMessage, googleMapsApi.isConfigured, t]);

  return (
    <Fieldset>
      <Grid gutter="md">
        <Grid.Col span={{ xs: 12, md: 6 }}>
          {loading ? (
            <CRUDCardSkeleton cardContentRows={15} />
          ) : (
            <Card>
              <Card.Section inheritPadding withBorder py="md">
                <Title order={4}> {t('CustomersPage:CRUD.contactInfo')}</Title>
              </Card.Section>
              <Card.Section inheritPadding py="md">
                <Grid>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('name')}
                      label={t('FieldName:name')}
                      placeholder={t('FieldName:name')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('lastName')}
                      label={t('FieldName:lastName')}
                      placeholder={t('FieldName:lastName')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <PhoneInput
                      inputComponent={CustomInput}
                      defaultCountry={
                        form.getValues().phoneCountryName || company?.country?.iso || 'MX'
                      }
                      country={form.getValues().phoneCountryName || company?.country?.iso || 'MX'}
                      onCountryChange={(countryCode) => {
                        form.setFieldValue('phoneCountryName', countryCode);
                      }}
                      onChange={(value) => {
                        form.setFieldValue('fullPhoneNumber', value);
                      }}
                      initialValueFormat="national"
                      // useNationalFormatForDefaultCountryValue={true}
                      countryCallingCodeEditable={false}
                      value={form.getValues().fullPhoneNumber}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('email')}
                      label={t('FieldName:email')}
                      placeholder={t('FieldName:email')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('notes')}
                      label={t('FieldName:notes')}
                      placeholder={t('FieldName:notes')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('privateNotes')}
                      label={t('FieldName:privateNotes')}
                      placeholder={t('FieldName:privateNotes')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <NumberInput
                      {...form.getInputProps('discount')}
                      placeholder={t('FieldName:discount')}
                      label={t('FieldName:discount')}
                      allowLeadingZeros={false}
                      allowNegative={false}
                      min={0}
                      max={100}
                      leftSection={<IconPercentage />}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <NumberInput
                      {...form.getInputProps('credit')}
                      placeholder={t('FieldName:credit')}
                      label={t('FieldName:credit')}
                      allowLeadingZeros={false}
                      allowNegative={false}
                      allowDecimal={true}
                      leftSection={<IconCurrencyDollar />}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <Group align="flex-end" gap="xs" wrap="nowrap">
                      <Box style={{ flex: 1 }}>
                        <AddressAutocomplete
                          {...form.getInputProps('address.street')}
                          label={t('FieldName:street')}
                          // description="Selecciona una opción para llenar el formulario"
                          placeholder={t('FieldName:street')}
                          onPlaceSelected={(place) => {
                            if (place) {
                              form.setFieldValue('address.street', place.street);
                              form.setFieldValue('address.neighborhood', place.neighborhood);
                              form.setFieldValue('address.postalCode', place.postalCode);
                              form.setFieldValue('address.city', place.city);
                              form.setFieldValue('address.state', place.state);
                              if (typeof place.lat === 'number' && typeof place.lng === 'number') {
                                form.setFieldValue(
                                  'address.mapLocation',
                                  buildMapLocationFromLatLng({
                                    lat: place.lat,
                                    lng: place.lng,
                                    source: 'places',
                                    formattedAddress: place.address,
                                    placeId: place.placeId,
                                  }),
                                );
                              }
                              form.setFieldValue('address.formattedAddress', place.address);
                            }
                          }}
                        />
                      </Box>
                      <Tooltip label={t('CustomersPage:selectMapLocation')}>
                        <ActionIcon
                          type="button"
                          aria-label={t('CustomersPage:selectMapLocation')}
                          variant="outline"
                          size="lg"
                          onClick={() => setIsMapModalOpen(true)}
                          disabled={isGoogleUnavailable}
                        >
                          <IconMapPin size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('address.neighborhood')}
                      label={t('FieldName:neighborhood')}
                      placeholder={t('FieldName:neighborhood')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('address.postalCode')}
                      label={t('FieldName:postalCode')}
                      placeholder={t('FieldName:postalCode')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('address.city')}
                      label={t('FieldName:city')}
                      placeholder={t('FieldName:city')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('address.state')}
                      label={t('FieldName:state')}
                      placeholder={t('FieldName:state')}
                    />
                  </Grid.Col>
                  {isEditing && (
                    <Grid.Col span={6}>
                      <Select
                        {...form.getInputProps('createdIn')}
                        label={t('FieldName:store')}
                        clearable={false}
                        allowDeselect={false}
                        placeholder={t('FieldName:store')}
                        data={
                          stores?.map((store) => {
                            return {
                              label: store.name,
                              value: store._id
                            };
                          }) || []
                        }
                      />
                    </Grid.Col>
                  )}
                  {isCompanyAdmin && (
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                      <Tooltip label={t('CustomersPage:isCorporateCustomerTooltip')}>
                        <Checkbox
                          {...form.getInputProps('isCorporateCustomer', { type: 'checkbox' })}
                          label={t('FieldName:isCorporateCustomer')}
                        />
                      </Tooltip>
                    </Grid.Col>
                  )}
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <Tooltip label={t('CustomersPage:loyaltyRedemptionDisabledTooltip')}>
                      <Checkbox
                        {...form.getInputProps('loyaltyRedemptionDisabled', { type: 'checkbox' })}
                        label={t('FieldName:loyaltyRedemptionDisabled')}
                      />
                    </Tooltip>
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          )}
        </Grid.Col>
        {canShowMexicanCFDIFields && (
          <Grid.Col span={{ xs: 12, md: 6 }}>
            {loading ? (
              <CRUDCardSkeleton cardContentRows={15} />
            ) : (
              <Card>
                <Card.Section inheritPadding withBorder py="md">
                  <Title order={4}> {t('CustomersPage:CRUD.invoice')}</Title>
                </Card.Section>
                <Card.Section inheritPadding py="md">
                  <Grid>
                    <Grid.Col span={12}>
                      <TextInput
                        {...form.getInputProps('rfc')}
                        label={t('FieldName:rfc')}
                        placeholder={t('FieldName:rfc')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        {...form.getInputProps('invoiceInfo.legal_name')}
                        label={t('FieldName:legal_name')}
                        placeholder={t('FieldName:legal_name')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Select
                        {...form.getInputProps('invoiceInfo.tax_system')}
                        searchable
                        label={t('FieldName:tax_system')}
                        placeholder={t('FieldName:tax_system')}
                        data={Object.keys(RegimenFiscalEnum).map((regimenFiscalCode) => {
                          const regimenFiscalKey = regimenFiscalCode as keyof typeof RegimenFiscalEnum;
                          return {
                            label: `${regimenFiscalCode} - ${RegimenFiscalEnum[regimenFiscalKey]}`,
                            value: regimenFiscalCode,
                            key: regimenFiscalCode
                          };
                        })}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <TextInput
                        {...form.getInputProps('invoiceInfo.zipCode')}
                        label={t('FieldName:zipCode')}
                        placeholder={t('FieldName:zipCode')}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Checkbox
                        {...form.getInputProps('invoiceInfo.applyIsrRetention', {
                          type: 'checkbox'
                        })}
                        label={t('FieldName:applyIsrRetention')}
                      />
                    </Grid.Col>
                  </Grid>
                </Card.Section>
              </Card>
            )}
          </Grid.Col>
        )}
        <Grid.Col span={12}>
          {loading ? (
            <CRUDCardSkeleton cardContentRows={3} />
          ) : (
            <Card>
              <Card.Section inheritPadding withBorder py="md">
                <Group gap="xs">
                  <Title order={4}>{t('CustomersPage:frequentCustomerCard.title')}</Title>
                  <Tooltip
                    label={t('CustomersPage:frequentCustomerCard.optional')}
                    opened={isCardHelpOpen}
                    multiline
                    w={280}
                    withArrow
                  >
                    <ActionIcon
                      type="button"
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={t('CustomersPage:frequentCustomerCard.help')}
                      onMouseEnter={() => setIsCardHelpOpen(true)}
                      onMouseLeave={() => setIsCardHelpOpen(false)}
                      onFocus={() => setIsCardHelpOpen(true)}
                      onBlur={() => setIsCardHelpOpen(false)}
                      onClick={() => setIsCardHelpOpen(true)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape' && isCardHelpOpen) {
                          event.stopPropagation();
                          setIsCardHelpOpen(false);
                        }
                      }}
                    >
                      <IconHelpCircle size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Card.Section>
              <Card.Section inheritPadding py="md">
                <Grid>
                  <Grid.Col span={12}>
                    <Flex
                      direction={{ base: 'column', sm: 'row' }}
                      align={{ base: 'stretch', sm: 'flex-end' }}
                      gap="xs"
                    >
                      {/* Keep this as text for physical cards and future barcode lookup/scanning. */}
                      <TextInput
                        {...form.getInputProps('frequentCustomerCard.number')}
                        label={t('CustomersPage:frequentCustomerCard.number')}
                        placeholder="0000-26"
                        flex={1}
                        miw={0}
                      />
                      <Button
                        type="button"
                        variant="light"
                        style={{ flexShrink: 0 }}
                        onClick={() => form.setFieldValue(
                          'frequentCustomerCard.number', generateFrequentCustomerCardNumber()
                        )}
                      >
                        {t('CustomersPage:frequentCustomerCard.generate')}
                      </Button>
                    </Flex>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      {...form.getInputProps('frequentCustomerCard.validFrom')}
                      type="date"
                      label={t('CustomersPage:frequentCustomerCard.validFrom')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      {...form.getInputProps('frequentCustomerCard.validThrough')}
                      type="date"
                      label={t('CustomersPage:frequentCustomerCard.validThrough')}
                    />
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          )}
        </Grid.Col>
        <Grid.Col span={12}>
          {loading ? (
            <CRUDCardSkeleton cardContentRows={5} />
          ) : (
            <Card>
              <Card.Section inheritPadding withBorder py="md">
                <Title order={4}>{t('CustomersPage:mapLocationSectionTitle')}</Title>
              </Card.Section>
              <Card.Section inheritPadding py="md">
                <Grid>
                  {isGoogleUnavailable ? (
                    <Grid.Col span={12}>
                      <Alert color="yellow">{googleUnavailableMessage}</Alert>
                    </Grid.Col>
                  ) : null}
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      label={t('FieldName:latitude')}
                      value={latitude !== null ? latitude.toFixed(6) : ''}
                      readOnly
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      label={t('FieldName:longitude')}
                      value={longitude !== null ? longitude.toFixed(6) : ''}
                      readOnly
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Group justify="space-between">
                      <Group>
                        <Button
                          type="button"
                          variant="default"
                          onClick={() => form.setFieldValue('address.mapLocation', null)}
                          disabled={!addressMapLocation}
                        >
                          {t('CustomersPage:clearMapLocation')}
                        </Button>
                      </Group>
                      {openInGoogleMapsUrl ? (
                        <Anchor href={openInGoogleMapsUrl} target="_blank" rel="noreferrer">
                          {t('CustomersPage:openInGoogleMaps')}
                        </Anchor>
                      ) : null}
                    </Group>
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          )}
        </Grid.Col>
      </Grid>
      <StoreMapLocationPickerModal
        opened={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        initialMapLocation={addressMapLocation}
        labelsNamespace="CustomersPage"
        onConfirm={({ lat, lng, source, formattedAddress, placeId, address }) => {
          if (address?.street || formattedAddress) {
            form.setFieldValue('address.street', address?.street || formattedAddress || '');
          }
          if (address?.neighborhood) {
            form.setFieldValue('address.neighborhood', address.neighborhood);
          }
          if (address?.postalCode) {
            form.setFieldValue('address.postalCode', address.postalCode);
          }
          if (address?.city) {
            form.setFieldValue('address.city', address.city);
          }
          if (address?.state) {
            form.setFieldValue('address.state', address.state);
          }
          if (formattedAddress) {
            form.setFieldValue('address.formattedAddress', formattedAddress);
          }
          form.setFieldValue(
            'address.mapLocation',
            buildMapLocationFromLatLng({
              lat,
              lng,
              source,
              formattedAddress,
              placeId,
            }),
          );
          setIsMapModalOpen(false);
        }}
      />
    </Fieldset>
  );
};

export default CreateEditForm;
